/**
 * The board's inventory bookkeeping: which free bet or booster is bound to which
 * option, which ones are therefore still available, and what every chip action
 * does to both.
 *
 * The RULES are the shared panel's, copied and not invented:
 *  - the two eligibility filters (`ChipPlacementPanel.tsx:891-912` for boosters,
 *    `:950-965` for free bets), including the `status === 'claimed'` guard that
 *    keeps an already-`applied` item from being re-bound and refused by Go's
 *    `ValidateInventoryItemsForBet`;
 *  - oldest-first FIFO, claimed on the FIRST chip an option receives
 *    (`:1282-1284` free bets, `:1436-1442` boosters), a free bet winning over a
 *    booster because the two never stack on one chip;
 *  - release on removal (`:1533-1548`), on clear (`:1553-1556`) and on a server
 *    refusal (`:2162-2170`), and restore-with-the-board on undo (`:1568-1569`).
 *
 * It is a SEPARATE state from `birdieChipReducer`'s, stepped in lockstep with it
 * by the board: every call takes the action plus the boards before and after, so
 * the two can never drift — an item is claimed only when the reducer actually
 * moved the board, and is released the moment its chip is gone, whichever of the
 * eleven actions took it.
 *
 * Pure: no store, no hooks, no `Date.now()`. Money is whatever unit the caller
 * already works in (decimal, as `ChipLimits` is).
 */

import type { Bonus, Booster, InventoryItem } from 'src/features/inventory/types/domain'
import { UNDO_DEPTH, type BirdieChipAction, type BirdieChipState } from './birdieChips'

/** Bound to one option: the inventory item id the bet will carry, and its boost. */
export interface ClaimedBooster {
  boosterId: string
  percent: number
}

/** Bound to one option: the free bet's item id, and the stake it fixes. */
export interface ClaimedFreeBet {
  freeBetId: string
  amount: number
}

export interface BirdieClaims {
  /** optionId → the booster whose percentage that option's price is shown at. */
  boosters: Readonly<Record<string, ClaimedBooster>>
  /** optionId → the free bet funding that option's chip. */
  freeBets: Readonly<Record<string, ClaimedFreeBet>>
}

export interface BirdieInventoryState extends BirdieClaims {
  /**
   * The undo stack, one snapshot per snapshot `birdieChipReducer` pushes. Held
   * here rather than inside `BirdieChipState` so the chip reducer keeps the
   * shape every other Birdie module already tests against; `applyChipAction`
   * pushes and pops it on exactly the actions the chip reducer does, and caps it
   * at the same `UNDO_DEPTH`, so index N here is always the board at index N
   * there.
   */
  history: readonly BirdieClaims[]
}

export const EMPTY_INVENTORY_STATE: BirdieInventoryState = {
  boosters: {},
  freeBets: {},
  history: []
}

/** What a placement may take. Never both: a free bet already fixes the stake. */
export interface PlannedClaim {
  booster?: ClaimedBooster
  freeBet?: ClaimedFreeBet
}

const applicable = (item: InventoryItem, gameId: string): boolean =>
  item.applicableGames.length === 0 || item.applicableGames.includes(gameId)

const byAge = (a: InventoryItem, b: InventoryItem): number =>
  new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()

/**
 * The boosters this board may bind, oldest first. `status === 'claimed'` is the
 * load-bearing one: an `applied` booster is still in the store while its bet
 * settles, and re-binding it earns an `InvalidInventoryItemError` from Go.
 */
export function availableBoosters(items: readonly InventoryItem[], gameId: string): Booster[] {
  return items
    .filter(
      (item): item is Booster =>
        item.type === 'booster' &&
        item.status === 'claimed' &&
        item.activationType === 'auto' &&
        item.boost.unit === 'percentage' &&
        applicable(item, gameId)
    )
    .sort(byAge)
}

/**
 * The free bets this board may bind, oldest first. A bonus outside the game's
 * own bet window would be refused at submit time, so it is skipped here rather
 * than offered and then rejected — silently, because the player cannot act on it
 * (the shared panel's own reasoning, `ChipPlacementPanel.tsx:1287`).
 */
export function availableFreeBets(
  items: readonly InventoryItem[],
  input: { gameId: string; currency: string; minBet: number; maxBet: number }
): Bonus[] {
  return items
    .filter(
      (item): item is Bonus =>
        item.type === 'bonus' &&
        item.status === 'claimed' &&
        item.currency === input.currency &&
        item.amount >= input.minBet &&
        item.amount <= input.maxBet &&
        applicable(item, input.gameId)
    )
    .sort(byAge)
}

const claimedIds = (claims: Readonly<Record<string, { freeBetId: string }>>): Set<string> => {
  const used = new Set<string>()
  for (const key of Object.keys(claims)) {
    const claim = claims[key]
    if (claim) used.add(claim.freeBetId)
  }
  return used
}

const claimedBoosterIds = (claims: Readonly<Record<string, ClaimedBooster>>): Set<string> => {
  const used = new Set<string>()
  for (const key of Object.keys(claims)) {
    const claim = claims[key]
    if (claim) used.add(claim.boosterId)
  }
  return used
}

/**
 * What this tap may take, asked BEFORE the placement because a free bet decides
 * the stake: the option locks to the free bet's amount rather than to the chip
 * the player has selected (`ChipPlacementPanel.tsx:1273-1281`).
 *
 * `optionMax` is Birdie's own constraint and the shared panel has no equivalent:
 * the board re-prices every round, so each option carries its own ceiling
 * (`maxPayout / multiplier`). A free bet is a FIXED stake and may not be quietly
 * cut down to fit one — a clamp is a refusal, not a quieter bet — so an option
 * whose ceiling is under the free bet is simply not offered it, and the next
 * free bet that does fit is taken instead. If none fits, the tap is an ordinary
 * cash placement and the free bet stays whole for an option that can take it.
 */
export function plannedClaim(input: {
  state: BirdieInventoryState
  optionId: string
  firstChipOnOption: boolean
  boosters: readonly Booster[]
  freeBets: readonly Bonus[]
  optionMax: number
}): PlannedClaim | undefined {
  if (!input.firstChipOnOption) return undefined

  const usedFreeBets = claimedIds(input.state.freeBets)
  for (const bonus of input.freeBets) {
    if (usedFreeBets.has(bonus.id)) continue
    if (bonus.amount > input.optionMax) continue
    return { freeBet: { freeBetId: bonus.id, amount: bonus.amount } }
  }

  const usedBoosters = claimedBoosterIds(input.state.boosters)
  for (const boost of input.boosters) {
    if (usedBoosters.has(boost.id)) continue
    return { booster: { boosterId: boost.id, percent: boost.boost.value } }
  }

  return undefined
}

const pruneTo = <T>(
  claims: Readonly<Record<string, T>>,
  chips: BirdieChipState['chips']
): { claims: Readonly<Record<string, T>>; changed: boolean } => {
  const next: Record<string, T> = {}
  let changed = false
  for (const optionId of Object.keys(claims)) {
    const claim = claims[optionId]
    if (claim === undefined) continue
    if (chips[optionId] === undefined) {
      changed = true
      continue
    }
    next[optionId] = claim
  }
  return { claims: changed ? next : claims, changed }
}

const snapshotOf = (state: BirdieInventoryState): BirdieClaims => ({
  boosters: state.boosters,
  freeBets: state.freeBets
})

const pushed = (state: BirdieInventoryState): readonly BirdieClaims[] => {
  const next = state.history.concat([snapshotOf(state)])
  return next.length > UNDO_DEPTH ? next.slice(next.length - UNDO_DEPTH) : next
}

/**
 * Settle the claims against what the chip reducer just did.
 *
 * `before` and `after` are the SAME object when the reducer refused the action —
 * that is how a refused placement is told from an accepted one, and it is why a
 * free bet is never spent on a chip that did not land.
 *
 * Whatever the action, the closing rule is the same: a claim whose option no
 * longer carries a chip is released. That one sweep covers a removal, a
 * server refusal, a repeat that dropped an option this round does not price and
 * a carry-forward that did the same.
 */
export function applyChipAction(
  state: BirdieInventoryState,
  input: {
    action: BirdieChipAction
    before: BirdieChipState
    after: BirdieChipState
    claim?: PlannedClaim
  }
): BirdieInventoryState {
  const { action, before, after, claim } = input
  const moved = before !== after

  // The board was wiped, so nothing can still be bound to it and no undo may
  // put a stake back — the chip reducer empties its own history on all three.
  if (action.type === 'clear' || action.type === 'restore') return EMPTY_INVENTORY_STATE
  if (action.type === 'roundReset' && !action.keep) return EMPTY_INVENTORY_STATE

  if (!moved) return state

  if (action.type === 'undo') {
    const previous = state.history[state.history.length - 1]
    const restored: BirdieInventoryState = {
      boosters: previous?.boosters ?? state.boosters,
      freeBets: previous?.freeBets ?? state.freeBets,
      history: state.history.slice(0, -1)
    }
    const boosters = pruneTo(restored.boosters, after.chips)
    const freeBets = pruneTo(restored.freeBets, after.chips)
    return { boosters: boosters.claims, freeBets: freeBets.claims, history: restored.history }
  }

  // `snapshot` only re-points the Repeat source; it moves no chip and takes no
  // claim with it.
  if (action.type === 'snapshot') return state

  // The chip reducer pushes an undo snapshot on exactly these four.
  const keepsHistory =
    action.type === 'place' ||
    action.type === 'remove' ||
    action.type === 'double' ||
    action.type === 'repeat'
  const history = keepsHistory ? pushed(state) : []

  let boosters = state.boosters
  let freeBets = state.freeBets
  if (action.type === 'place' && claim) {
    if (claim.freeBet) freeBets = { ...freeBets, [action.optionId]: claim.freeBet }
    else if (claim.booster) boosters = { ...boosters, [action.optionId]: claim.booster }
  }

  return {
    boosters: pruneTo(boosters, after.chips).claims,
    freeBets: pruneTo(freeBets, after.chips).claims,
    history
  }
}

/**
 * The options whose stake is a free bet. Handed to `ChipLimits` so the reducer
 * can refuse a top-up on one, leave it out of a ×2 and drop rather than clamp it
 * across a round — and to `chargeableStake`, so the wallet is never asked for a
 * stake the reward already paid.
 */
export function freeBetOptionIds(claims: BirdieClaims): Set<string> {
  return new Set(Object.keys(claims.freeBets))
}

/** optionId → boost percentage, for the tiles whose price shows one. */
export function boosterPercentById(claims: BirdieClaims): Record<string, number> {
  const out: Record<string, number> = {}
  for (const optionId of Object.keys(claims.boosters)) {
    const claim = claims.boosters[optionId]
    if (claim) out[optionId] = claim.percent
  }
  return out
}

/** `mult * (1 + percent / 100)` — ChipPlacementPanel.tsx:1057-1059. */
export function boostedMultiplier(multiplier: number, percent: number): number {
  return percent > 0 ? multiplier * (1 + percent / 100) : multiplier
}

/**
 * The prices the Potential Win pill is computed from: the round's, with the
 * inventory boost applied to the ONE option each booster is bound to — which is
 * how Go will consume it on the matching RegisterBet.
 *
 * Returns the map it was given when there is nothing to boost, so the board's
 * `totals` memo does not recompute on every render.
 */
export function boostedMultipliers(
  base: ReadonlyMap<string, number>,
  claims: BirdieClaims
): ReadonlyMap<string, number> {
  const optionIds = Object.keys(claims.boosters)
  if (optionIds.length === 0) return base
  const next = new Map(base)
  for (const optionId of optionIds) {
    const claim = claims.boosters[optionId]
    const multiplier = base.get(optionId)
    if (!claim || multiplier === undefined) continue
    next.set(optionId, boostedMultiplier(multiplier, claim.percent))
  }
  return next
}
